import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import './consentform.css';
import arrow from '../Assets/images/arrow-right-clinic.png'
import CompanyLogo from '../Assets/images/AyurAI_logo_filmnoir.png'

const studyContent = {
  GMW: {
    studyTitle:
      "To Evaluate the Effectiveness of a Whole-System Medicine Approach-Based AIWeLL™ Protocol on Well-Being: A Single-Arm Pre-Post Study",
    sections: [
      {
        title: "About the Study",
        text: "You are invited to participate in a research study designed to evaluate the effectiveness of the AIWeLL™ Protocol on overall well-being. This study integrates Ayurvedic principles with modern diagnostics to offer a personalized wellness plan tailored to individual health needs.",
      },
      {
        title: "Why This Study?",
        text: "Many chronic and lifestyle-related conditions are managed using symptom-focused approaches. This study investigates whether a more holistic method - combining diet, lifestyle changes, herbal or nutritional supplements, and mind-body practices, can improve overall wellness. Specifically, it aims to support: Gastric well-being, Metabolic well-being, Women’s well-being.",
      },
      {
        title: "What Will Participation Involve?",
        text: "Participants will receive a customized treatment plan including:",
        list: [
          "Doctor-prescribed herbal medicines",
          "Ayurvedic diet and lifestyle guidance",
          "Recommendations for yoga, physical activity, and meditation",
          "Monitoring of your blood markers and gut microbiome data to ensure optimal results and progress tracking",
          "As a study participant, you will be provided blood tests and gut microbiome tests worth over ₹15,000 at no cost; ₹100 per doctor consult compensation; you will only need to purchase the herbal medicines required for the treatment.",
        ],
      },
    ],
  },
  DM: {
    studyTitle:
      "To Evaluate the Effectiveness of a Whole-System Medicine Approach-Based AIWeLL DM™ Protocol on Diabetes: A Single-Arm Pre-Post Study",
    sections: [
      {
        title: "About the Study",
        text: "You are invited to take part in a research study evaluating a new approach to managing Type 2 Diabetes and prediabetes using the AIWeLL DM™ Protocol - a combination of Ayurvedic principles and modern diagnostics to deliver a personalized, holistic treatment.",
      },
      {
        title: "Why This Study?",
        text: "Diabetes affects millions globally, and conventional treatments often fall short in long-term management. This study explores if a Whole-System Medicine (WSM) approach can: Improve blood sugar control, Reduce insulin resistance and inflammation, Enhance overall health and well-being.",
      },
      {
        title: "What Will Participation Involve?",
        text: "Participants will receive a customized treatment plan including:",
        list: [
          "Doctor-prescribed herbal medicines",
          "Ayurvedic diet and lifestyle guidance",
          "Recommendations for yoga, physical activity, and meditation",
          "Monitoring of your blood markers and Continuous Glucose Monitor (CGM) data to ensure optimal results and progress tracking",
          "As a study participant, you will be provided blood tests and a continuous glucose monitor (CGM) worth over ₹15,000 at no cost; ₹100 per doctor consult compensation; you will only need to purchase the herbal medicines required for the treatment.",
        ],
      },
    ],
  },
};

const generalSections = [
  {
    title: "Location & Duration",
    text: "The study will be conducted through online consultations facilitated by the Outpatient Department (OPD) of Sri Sarada Ayurveda Clinic, Chennai. Duration: Up to 6 months.",
  },
  {
    title: "Voluntary Participation",
    text: "Your participation is entirely voluntary. If you have any doubts or require clarification, feel free to speak with Dr. Gangadevi or the Ayur.AI team.",
  },
  {
    title: "Confidentiality",
    text: "Your identity will remain strictly confidential. While data may be shared with the study sponsor, regulators, or in publications, your name or personal details will never be disclosed. Regulatory bodies and the study sponsor may access some of your medical records strictly for audit and regulatory purposes, while maintaining your privacy.",
  },
  {
    title: "Adverse Effects",
    text: "If you experience any negative effects during the study, contact clinicals@ayurai.io immediately.",
  },
  {
    title: "Withdrawal or Termination",
    list: [
      "You experience side effects that require withdrawal",
      "The investigator believes continued participation may harm you",
      "You require medications not permitted during the study",
    ],
  },
];

const consentChecklist = [
  "I confirm that I have read and understood the information on the study and have had the opportunity to ask questions. The study doctor and study team has explained the study in detail and have clarified all my doubts.",
  "I understand that my participation in the study is voluntary and that I am free to withdraw at any time, without giving any reason, without my medical care or legal rights being affected.",
  "I understand that the Sponsor of the clinical trial, others working on the Sponsor’s behalf, the Ethics Committee and the regulatory authorities will not need my permission to look at my health records both in respect of the current study and any further research that may be conducted in relation to it, even if I withdraw from the trial. I agree to this access. However, I understand that my identity will not be revealed in any information released to third parties or published.",
  "I agree not to restrict the use of any data or results that arise from this study provided such a use is only for scientific purpose(s).",
  "I agree to take part in the above study.",
];

const pdfCss = `
body {
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 11pt;
  line-height: 1.5;
  margin: 0;
  padding: 0;
  background-color: #fff;
  color: #000;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

@page {
  size: A4;
  margin: 20mm 15mm;
}

.container {
  width: 80%;
  margin: 0 auto;
  padding: 20px;
  background-color: #fff;
  border: none;
  box-shadow: none;
}

.title {
  text-align: center;
  color: #000;
  margin-bottom: 25pt;
  font-size: 18pt;
  font-weight: bold;
  border-bottom: 2px solid #000;
  padding-bottom: 10pt;
}

.subtitle {
  color: #000;
  margin-top: 25pt;
  margin-bottom: 15pt;
  border-bottom: 1px solid #ccc;
  padding-bottom: 5pt;
  font-size: 14pt;
  font-weight: bold;
}

.study-title {
  font-size: 14pt;
  font-weight: bold;
  margin-bottom: 15pt;
  color: #333;
}

.section-title {
  font-size: 13pt;
  font-weight: bold;
  margin-top: 18pt;
  margin-bottom: 8pt;
  color: #333;
}

.content-block {
  margin-bottom: 20pt;
  padding: 0;
  background-color: transparent;
  border-radius: 0;
}

.content-block p,
.content-block li,
.general-section p,
.general-section li,
.section-text,
.section-list li {
  margin-bottom: 8pt;
  text-align: justify;
}

.content-block strong {
  font-weight: bold;
  color: #000;
}

ul.section-list {
    padding-left: 20pt;
    list-style-type: disc;
}

.consent {
  margin-top: 30pt;
}

.consent-form {

}

.checkbox-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 10pt;
  padding: 3pt 0;
  border: none;
  background-color: transparent;
  page-break-inside: avoid;
}

.checkbox-item input[type="checkbox"] {
   margin-right: 8pt;
   margin-top: 1pt;
   flex-shrink: 0;
   width: 12pt;
   height: 12pt;
   vertical-align: middle;

}

.signature {
  margin-top: 35pt;
  padding-top: 15pt;
  border-top: 1px solid #ccc;
  page-break-inside: avoid;
}

.signature label {
    display: block;
    font-weight: bold;
    margin-bottom: 5pt;
    color: #000;
}

.signature-typed-name {
    display: block;
    margin-top: 5pt;
    margin-bottom: 20pt;
    font-size: 11pt;
    font-style: italic;
    border: 1px solid #eee;
    padding: 5pt;
    min-height: 1.5em;
}

.signature-line {
  border-bottom: 1px solid #000;
  height: 25pt;
  margin-top: 30pt;
  margin-bottom: 5pt;
  width: 80%;
  max-width: 300pt;
}
.signature-line-label {
   display: block;
   font-size: 9pt;
   color: #555;
}

.submit-button,
.signature-input {
  display: none !important;
}
`;

export default function ParticipantInfoGuide() {
  const containerRef = useRef(null);
  const [user, setUser] = useState(null);
  const [checkedItems, setCheckedItems] = useState(
    Array(consentChecklist.length).fill(false)
  );
  const [signatureName, setSignatureName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const prevAllChecked = useRef(false);

  const getUid = () => new URLSearchParams(window.location.search).get("uid");
  
  const uid = getUid();

  useEffect(() => {
    if (!uid) return;

    axios
      .get(
        `https://2mwtcttdj4.execute-api.ap-south-1.amazonaws.com/dev/users/${uid}`
      )
      .then((response) => {
        setUser(response.data);
      })
      .catch((error) => {
        console.error("Fetch error:", error);
      });
  }, [uid]);

  const handleCheck = (index) => {
    const newChecked = [...checkedItems];
    newChecked[index] = !newChecked[index];
    setCheckedItems(newChecked);
  };

  const allChecked = checkedItems.every(Boolean);

  // useEffect(() => {
  //   if (user && !prevAllChecked.current && allChecked) {
  //     axios
  //       .post(
  //         // `https://2mwtcttdj4.execute-api.ap-south-1.amazonaws.com/dev/${uid}/consentSign`,
  //         `http://localhost:5000/${uid}/consentSign`,
  //         {
  //           consentSign: false,
  //         }
  //       )
  //       .then((res) => {
  //         console.log("Consent sign updated (intermediate):", res.data);
  //       })
  //       .catch((err) => {
  //         console.error("ConsentSign POST error (intermediate):", err);
  //       });
  //   }
  //   prevAllChecked.current = allChecked;
  // }, [allChecked, user, uid]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allChecked || !signatureName || !containerRef.current) return;

    setSubmitting(true);

    let finalHtmlBody = "";
    const participantIdentifier =
      user?.name || signatureName || uid || "Participant";
    console.log(user?.data?.SubjectId);

    try {
      const containerElement = containerRef.current;
      let htmlBody = containerElement.outerHTML;

      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlBody, "text/html");
      const bodyContent = doc.body.firstChild;

      if (!bodyContent) {
        throw new Error("Could not parse container HTML for PDF generation.");
      }

      const titleElement = bodyContent.querySelector(".title strong");
      if (titleElement) {
        titleElement.textContent = `PARTICIPANT CONSENT FORM - ${participantIdentifier}`;
      } else {
        const h1Title = bodyContent.querySelector("h1.title");
        if (h1Title) {
          h1Title.innerHTML = `<strong>PARTICIPANT CONSENT FORM - ${participantIdentifier}</strong>`;
        }
      }

      const checkboxes = bodyContent.querySelectorAll(
        '.consent-form input[type="checkbox"]'
      );
      checkboxes.forEach((checkbox) => {
        checkbox.setAttribute("checked", "checked");
      });

      const signatureDiv = bodyContent.querySelector(".signature");
      const signatureInput = bodyContent.querySelector(".signature-input");

      if (signatureDiv) {
        if (signatureInput) {
          signatureInput.remove();
        }
        const signatureLabel = bodyContent.querySelector(
          'label[for="signatureInput"]'
        );
        if (signatureLabel) {
          signatureLabel.textContent = "Participant Name (Typed):";
        } else {
          const newLabel = document.createElement("label");
          newLabel.textContent = "Participant Name (Typed):";
          signatureDiv.insertBefore(newLabel, signatureDiv.firstChild);
        }

        const typedNamePara = document.createElement("div");
        typedNamePara.className = "signature-typed-name";
        typedNamePara.textContent = signatureName
          ? signatureName
          : "(No name typed)";
        const labelElement = signatureDiv.querySelector("label");
        if (labelElement) {
          labelElement.parentNode.insertBefore(
            typedNamePara,
            labelElement.nextSibling
          );
        } else {
          signatureDiv.appendChild(typedNamePara);
        }


        const dateLineLabel = document.createElement("span");
        dateLineLabel.className = "signature-line-label";
        dateLineLabel.textContent = `Date = ${new Date().toISOString().split("T")[0]}`;
        signatureDiv.appendChild(dateLineLabel);
      }

      const submitButton = bodyContent.querySelector(".submit-button");

      if (submitButton) {
        submitButton.remove();
      }

      finalHtmlBody = bodyContent.outerHTML;
    } catch (domError) {
      console.error("Error processing HTML for PDF:", domError);
      setSubmitting(false);
      alert("Failed to prepare the consent form for saving. Please try again.");
      return;
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8"/>
        <title>Consent PDF - ${participantIdentifier}</title>
        <style>${pdfCss}</style>
      </head>
      <body>${finalHtmlBody}</body>
      </html>
    `;

    try {
      console.log({
        consentSign: true,
        signature: signatureName,
        htmlContent: htmlContent,
      })
      const res = await axios.put(
        `https://2mwtcttdj4.execute-api.ap-south-1.amazonaws.com/dev/update/sign/${uid}`,
        {
          consentSign: true,
          signature: signatureName,
          htmlContent: htmlContent,
        }
      );
      console.log("Consent, signature, and HTML content updated:", res.data);
      setSubmitted(true);
    } catch (err) {
      console.error("Submit error:", err);
      alert(
        `Submission failed: ${
          err.message || "Please check console for details."
        }`
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p>Loading participant information...</p>
      </div>
    );
  }

  const studyType = user?.clinicalStudy;
  let studyCategory;
  
  if (["Gastro", "Metabolic", "Women"].includes(studyType)) {
    studyCategory = "GMW";
  } else if (studyType === "Diabetes") {
    studyCategory = "DM";
  } else {
    console.warn(
      `Unexpected clinicalStudy type: ${studyType}. Defaulting to GMW content.`
    );
    studyCategory = "GMW";
  }

  const { studyTitle, sections } =
    studyContent[studyCategory] || studyContent.GMW;

  return (
    <div className="Concent-container p-10" ref={containerRef}>
      <div>
         <div className="centered-logo-abc789">
          <img src={CompanyLogo} alt="Company Logo" className="Company-logo" />
        </div>
        <h1 className="Concent-title">
          <strong>Participant Information Guide</strong>
        </h1>
      </div>
      <h2 className="study-title mb-10">{studyTitle}</h2>
      <div className="content-block-final">
        {sections.map((sec, idx) => (
          <div key={`study-sec-${idx}`} className="section mt-4">
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }} className="mb-2">
              <img src={arrow} alt="icon" />
              <h3 className="section-title">{sec.title}</h3>
            </div>

            {sec.text && <p className="section-text ml-10">{sec.text}</p>}
            {sec.list && (
              <ul className="section-list ml-14">
                {sec.list.map((li, i) => (
                  <li key={`study-li-${idx}-${i}`}>{li}</li>
                ))}
              </ul>
            )}
            
          </div>
        ))}
        {generalSections.map((sec, idx) => (
          <div key={`gen-sec-${idx}`} className="general-section mt-4">
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }} className="mb-2">
              <img src={arrow} alt="icon" />
              <h3 className="section-title">{sec.title}</h3>
            </div>

            {sec.text && <p className="section-text ml-10">{sec.text}</p>}
            
            {sec.list && (
              <ul className="section-list ml-14">
                {sec.list.map((li, i) => (
                  <li key={`gen-li-${idx}-${i}`}>{li}</li>
                ))}
              </ul>
            )}
          </div>
        ))}

      </div>

      <div className="consent">
        <form className="consent-form" onSubmit={handleSubmit}>
          <h2 className="Concent-subtitle">PATIENT CONSENT</h2>
          {consentChecklist.map((item, idx) => (
            <label key={`consent-item-${idx}`} className="checkbox-item">
              <input
                type="checkbox"
                checked={checkedItems[idx]}
                onChange={() => handleCheck(idx)}
              />
              {item}
            </label>
          ))}

          {allChecked && (
            <div className="signature">
              <label htmlFor="signatureInput" className="label-input">
                Signature of the participant (Type your full name):
              </label>
              <input
                id="signatureInput"
                type="text"
                value={signatureName}
                onChange={(e) => setSignatureName(e.target.value)}
                placeholder="Type your full name here to sign"
                className="signature-input"
                required
              />
            </div>
          )}

          <button
            type="submit"
            className="Concent-submit-button"
            disabled={!allChecked || !signatureName || submitting || submitted}
          >
            {submitted
              ? "Submitted Successfully"
              : submitting
              ? "Submitting..."
              : "Confirm Consent and Submit"}
          </button>
        </form>
      </div>
    </div>
  );
}
